import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {
  Box,
  Button,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';

import { fetchProductInventoryLogs } from '@sophocles/api/productInventoryLog';
import { editProductInventory } from '@sophocles/features/warehouse/products/api';

import { Metric, SectionLabel, TagChip } from './detailDrawerPrimitives';
import { PullDialog, ReceiveDialog } from './ProductInventoryDialogs';

const HISTORY_LIMIT = 10;

/**
 * Right-side detail panel for a Product row. Shows quantities, indicators,
 * components, recent inventory history, and inline Receive/Pull actions.
 * The "Full Detail" link routes to the existing product form view.
 *
 * Props:
 *   product   – row data; null while drawer is closed
 *   open      – boolean
 *   onClose   – () => void
 *   onMutated – () => void  called after Receive/Pull to refresh parent grid
 */
export default function ProductDetailDrawer({ product, open, onClose, onMutated }) {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [receiveOpen, setReceiveOpen] = useState(false);
  const [pullOpen, setPullOpen] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const productId = product?.id ?? null;

  const loadHistory = useCallback(() => {
    if (!productId) {
      setHistory([]);
      return undefined;
    }
    let cancelled = false;
    setHistoryLoading(true);
    fetchProductInventoryLogs({
      product: productId,
      o: '-action_time',
      limit: HISTORY_LIMIT,
    })
      .then(data => {
        if (cancelled) return;
        const rows = Array.isArray(data) ? data : (data?.results ?? []);
        setHistory(rows);
      })
      .catch(() => {
        if (!cancelled) setHistory([]);
      })
      .finally(() => {
        if (!cancelled) setHistoryLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [productId]);

  useEffect(() => {
    if (!open) return undefined;
    return loadHistory();
  }, [open, loadHistory]);

  if (!product) {
    return <Drawer anchor="right" open={open} onClose={onClose} />;
  }

  const {
    id,
    item_part_num,
    item_name,
    item_description,
    qty_available = 0,
    qty_ordered = 0,
    qty_warehoused = 0,
    inventory,
    components = [],
    has_radios,
    finished_good,
    obsolete,
    low_stock_threshold,
  } = product;

  // Backend already nets out qty_on_new_orders, so qty_available is the
  // truly-available count (negative on back order).
  const available = qty_available;
  const stockState =
    available <= 0
      ? { color: 'error.main', label: t('warehouse.product.stockOutOfStock', 'Out of stock') }
      : low_stock_threshold != null && available <= low_stock_threshold
        ? { color: 'warning.main', label: t('warehouse.product.stockLowStock', 'Low stock') }
        : { color: 'info.main', label: t('warehouse.product.stockInStock', 'In stock') };

  const refreshAfterMutation = () => {
    onMutated?.();
    loadHistory();
  };

  const handleReceive = ({ source, shipmentNumber, qtyReceived }) => {
    if (!inventory?.id) return;
    const baseQty = components.length > 0 ? 0 : inventory.qty_on_hand;
    const newQty = baseQty + qtyReceived;
    dispatch(
      editProductInventory({
        id: inventory.id,
        qty_on_hand: newQty,
        action: 'RECEIVE',
        reason: shipmentNumber
          ? `Received ${qtyReceived}: ${source || 'manual'} with shipment #${shipmentNumber}`
          : source
            ? `Received ${qtyReceived}: ${source}`
            : `Received ${qtyReceived}`,
      }),
    ).then(refreshAfterMutation);
  };

  const handlePull = ({ quantityPulled, reason }) => {
    if (!inventory?.id) return;
    const newQty = inventory.qty_on_hand - quantityPulled;
    dispatch(
      editProductInventory({
        id: inventory.id,
        action: 'PULL',
        qty_on_hand: newQty,
        reason: reason ? `Pulled ${quantityPulled}: ${reason}` : `Pulled ${quantityPulled}`,
      }),
    ).then(refreshAfterMutation);
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: 520 },
          bgcolor: 'var(--card-bg)',
          borderLeft: 1,
          borderColor: 'var(--md-outline-variant)',
        },
      }}
    >
      <Stack
        direction="row"
        alignItems="flex-start"
        sx={{ px: 'var(--page-gap)', py: 2, borderBottom: 1, borderColor: 'var(--md-outline-variant)' }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.25 }}>
            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: stockState.color, flexShrink: 0 }} />
            <Typography variant="caption" sx={{ fontFamily: 'monospace', color: 'text.secondary', letterSpacing: 0.2 }}>
              {item_part_num}
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.disabled' }}>
              · {stockState.label}
            </Typography>
          </Stack>
          <Typography sx={{ fontSize: 'var(--title-size)', fontWeight: 'var(--title-weight)', lineHeight: 1.2 }}>
            {item_name}
          </Typography>
          {item_description && (
            <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.25 }}>
              {item_description}
            </Typography>
          )}
        </Box>
        <IconButton onClick={onClose} aria-label={t('actions.close', 'Close')} size="small" sx={{ ml: 1 }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Stack>

      <Box sx={{ px: 'var(--page-gap)', py: 2 }}>
        <SectionLabel accent={stockState.color}>{t('warehouse.quantities', 'Quantities')}</SectionLabel>
        <Stack direction="row" spacing={3} sx={{ mt: 1 }}>
          <Metric
            label={t('warehouse.product.availableHeader', 'Available')}
            value={available}
            accent={stockState.color}
            emphasis
          />
          <Metric label={t('warehouse.product.onOrderHeader', 'On Order')} value={qty_ordered ?? 0} />
          <Metric
            label={t('warehouse.product.warehousedHeader', 'Warehoused')}
            value={qty_warehoused ?? inventory?.qty_on_hand ?? 0}
          />
        </Stack>

        {(has_radios || finished_good || obsolete) && (
          <Stack direction="row" spacing={0.75} sx={{ mt: 2, flexWrap: 'wrap' }}>
            {has_radios && <TagChip color="info.main" label={t('warehouse.product.hasRadios', 'Has Radios')} />}
            {finished_good && (
              <TagChip color="success.main" label={t('warehouse.product.finishedGood', 'Finished Good')} />
            )}
            {obsolete && <TagChip color="text.secondary" label={t('warehouse.product.obsolete', 'Obsolete')} />}
          </Stack>
        )}
      </Box>

      {components.length > 0 && (
        <Box sx={{ px: 'var(--page-gap)', pb: 2, borderTop: 1, borderColor: 'var(--md-outline-variant)', pt: 2 }}>
          <SectionLabel>{t('warehouse.product.componentsTitle', 'Components')}</SectionLabel>
          <List dense disablePadding sx={{ mt: 0.5 }}>
            {components.map(c => {
              const name = c.child_product_detail?.item_name ?? c.child_product?.item_name ?? '—';
              const sku = c.child_product_detail?.item_part_num ?? c.child_product?.item_part_num;
              return (
                <ListItem key={c.id ?? c.child_product?.id} disableGutters sx={{ py: 0.5 }}>
                  <ListItemText
                    primary={
                      <Stack direction="row" spacing={1} alignItems="baseline">
                        {sku && (
                          <Typography
                            variant="caption"
                            sx={{ fontFamily: 'monospace', color: 'text.secondary', minWidth: 80 }}
                          >
                            {sku}
                          </Typography>
                        )}
                        <Typography variant="body2">{name}</Typography>
                      </Stack>
                    }
                    secondary={
                      <Typography component="span" variant="caption" sx={{ color: 'text.secondary' }}>
                        {t('warehouse.qtyPer', 'Qty per: {{count}}', { count: c.quantity })}
                      </Typography>
                    }
                  />
                </ListItem>
              );
            })}
          </List>
        </Box>
      )}

      <Box
        sx={{
          px: 'var(--page-gap)',
          py: 2,
          flex: 1,
          overflowY: 'auto',
          borderTop: 1,
          borderColor: 'var(--md-outline-variant)',
        }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <SectionLabel>{t('warehouse.recentHistory', 'Recent History')}</SectionLabel>
          <Button
            component={Link}
            to={{
              pathname: '/sales/reports/inventory-log',
              search: new URLSearchParams(id ? { product: String(id) } : {}).toString(),
            }}
            size="small"
            sx={{ minWidth: 0, textTransform: 'none', fontSize: '0.75rem' }}
          >
            {t('warehouse.viewAll', 'View all')}
          </Button>
        </Stack>
        {historyLoading ? (
          <Stack spacing={1} sx={{ mt: 1 }}>
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} variant="rounded" height={44} />
            ))}
          </Stack>
        ) : history.length === 0 ? (
          <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>
            {t('warehouse.noHistoryYet', 'No history yet.')}
          </Typography>
        ) : (
          <List dense disablePadding sx={{ mt: 0.5 }}>
            {history.map(entry => (
              <HistoryRow key={entry.id} entry={entry} />
            ))}
          </List>
        )}
      </Box>

      <Stack
        direction="row"
        spacing={1}
        sx={{
          px: 'var(--page-gap)',
          py: 1.5,
          borderTop: 1,
          borderColor: 'var(--md-outline-variant)',
          justifyContent: 'space-between',
        }}
      >
        <Button
          component={Link}
          to={`/sales/products/${id}`}
          variant="text"
          size="small"
          endIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
        >
          {t('warehouse.fullDetail', 'Full Detail')}
        </Button>
        <Stack direction="row" spacing={1}>
          {!obsolete && (
            <>
              <Button variant="outlined" size="small" onClick={() => setPullOpen(true)}>
                {t('warehouse.product.pull', 'Pull')}
              </Button>
              <Button variant="contained" size="small" onClick={() => setReceiveOpen(true)}>
                {t('warehouse.product.receive', 'Receive')}
              </Button>
            </>
          )}
        </Stack>
      </Stack>

      <ReceiveDialog open={receiveOpen} onClose={() => setReceiveOpen(false)} onReceive={handleReceive} />
      <PullDialog open={pullOpen} onClose={() => setPullOpen(false)} onPull={handlePull} />
    </Drawer>
  );
}

function HistoryRow({ entry }) {
  const { t } = useTranslation();
  const when = entry.action_time ? new Date(entry.action_time).toLocaleString() : '—';
  const user =
    entry.user?.first_name && entry.user?.last_name ? `${entry.user.first_name} ${entry.user.last_name}` : null;
  // Color cues per action — keeps the log scannable.
  const actionColorMap = {
    RECEIVE: 'success.main',
    PULL: 'warning.main',
    ORDER: 'info.main',
    UPDATE: 'text.secondary',
  };
  const actionColor = actionColorMap[entry.action] ?? 'text.disabled';

  return (
    <ListItem disableGutters sx={{ alignItems: 'flex-start', py: 0.75 }}>
      <Box
        sx={{
          width: 3,
          alignSelf: 'stretch',
          borderRadius: 1,
          bgcolor: actionColor,
          mr: 1.25,
          flexShrink: 0,
        }}
      />
      <ListItemText
        sx={{ my: 0 }}
        primary={
          <Stack direction="row" spacing={1} alignItems="baseline">
            <Typography
              variant="caption"
              sx={{
                fontSize: '0.65rem',
                fontWeight: 700,
                letterSpacing: 0.5,
                color: actionColor,
                textTransform: 'uppercase',
              }}
            >
              {entry.action || '—'}
            </Typography>
            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
              {t('warehouse.qtyAbbrev', 'qty {{value}}', { value: entry.qty })}
            </Typography>
          </Stack>
        }
        secondary={
          <Stack component="span" sx={{ display: 'block', mt: 0.25 }}>
            {entry.reason && (
              <Typography component="span" variant="caption" sx={{ display: 'block', color: 'text.primary' }}>
                {entry.reason}
              </Typography>
            )}
            <Typography
              component="span"
              variant="caption"
              sx={{ display: 'block', color: 'text.secondary', fontSize: '0.7rem' }}
            >
              {when}
              {user ? ` · ${user}` : ''}
            </Typography>
          </Stack>
        }
      />
    </ListItem>
  );
}
